import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular';
import * as moment from 'moment';
import { finalize } from 'rxjs';
import { DATETIMEFORMART, Place, ResourceBusySchedule } from 'src/app/interfaces/interfaces';
import { ApiService } from 'src/app/services/api-services/api.service';
import { DatetimeFormatService } from 'src/app/services/datetime-format-service/datetime-format.service';
import { StringInfoMessageService } from 'src/app/services/string-info-message/string-info-message.service';
import { UserStore } from 'src/app/stores/user.store';
import { EventComponent } from '../event/event.component';

@Component({
  selector: 'app-resource-availability',
  templateUrl: './resource-availability.component.html',
  styleUrls: ['./resource-availability.component.scss'],
})
export class ResourceAvailabilityComponent implements OnInit {
  @Input() place: Place;

  busySchedule: ResourceBusySchedule = {};
  isLoading = false;
  locale = this.dateTimeFormatService.selectedDateTimeFormat;
  address = '';
  currentWeekStart: Date;

  constructor(
    private modalController: ModalController,
    private apiService: ApiService,
    private dateTimeFormatService: DatetimeFormatService,
    private stringInfoMessageService: StringInfoMessageService,
    protected userStore: UserStore,
    private router: Router,
  ) {}

  ngOnInit() {
    this.address = this.stringInfoMessageService.buildAddressString(this.place);
    this.currentWeekStart = this.getWeekStart(new Date());
    this.fetchWeek(this.currentWeekStart);
  }

  fetchWeek(weekStart: Date) {
    if (!this.place?.emailAddress) {
      return;
    }

    this.currentWeekStart = weekStart;
    const start = moment(weekStart).startOf('day').utc().toISOString();
    const end = moment(weekStart)
      .add(7, 'days')
      .startOf('day')
      .utc()
      .toISOString();

    this.isLoading = true;
    this.apiService
      .getPlaceBusySchedule(this.place.emailAddress, start, end)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (busySchedule) => {
          this.busySchedule = busySchedule || {};
        },
        error: () => {
          this.busySchedule = {};
        },
      });
  }

  async openBookingForm(slot: { start: Date; end: Date; isBetweenBusySlots?: boolean; maxEnd?: Date }) {
    const selectedMinutes = moment(slot.end).diff(moment(slot.start), 'minutes');
    const defaultEnd = selectedMinutes > 30 ? moment(slot.end) : moment(slot.start).add(30, 'minutes');
    const maxEnd = slot.maxEnd ? moment(slot.maxEnd) : defaultEnd;
    const finalEnd = defaultEnd.isBefore(maxEnd) ? defaultEnd : maxEnd;

    const modal = await this.modalController.create({
      component: EventComponent,
      initialBreakpoint: 0.95,
      breakpoints: [0, 0.95],
      handle: false,
      canDismiss: async (data, role) => role != 'gesture',
      componentProps: {
        place: this.place,
        purpose: 'slotBook',
        slotStartTime: moment(slot.start).format(DATETIMEFORMART),
        slotEndTime: finalEnd.format(DATETIMEFORMART),
        slotMaxEndTime: maxEnd.format(DATETIMEFORMART),
        isSlotBetweenBusySlots: slot.isBetweenBusySlots ?? false,
      },
    });

    modal.onDidDismiss().then((result) => {
      if (result.data?.bookingCreated) {
        this.modalController.dismiss();
        this.router.navigate(['tabs/bookings'], { replaceUrl: true });
      }
    });

    modal.present();
  }

  dismiss() {
    this.modalController.dismiss();
  }

  get statusColor() {
    if (this.place?.pending) {
      return 'warning';
    }

    return this.place?.isAvailable ? 'success' : 'danger';
  }

  get statusIcon() {
    if (this.place?.pending) {
      return 'time-outline';
    }

    return this.place?.isAvailable
      ? 'checkmark-circle-outline'
      : 'alert-circle-outline';
  }

  get statusLabel() {
    if (this.place?.pending) {
      return 'resourceAvailability.pending';
    }

    return this.place?.isAvailable
      ? 'resourceAvailability.available'
      : 'resourceAvailability.busy';
  }

  private getWeekStart(date: Date) {
    const weekStart = new Date(date);
    const dayOfWeek = weekStart.getDay();
    const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    weekStart.setDate(weekStart.getDate() - daysFromMonday);
    weekStart.setHours(0, 0, 0, 0);
    return weekStart;
  }
}
